import { CheckCircle2, AlertTriangle, XOctagon } from "lucide-react";
import { Card, Badge } from "@/components/ui/primitives";
import type { PolicyEvaluationDTO } from "@/lib/api";
import { ManualCitationLink } from "@/components/ManualCitationDialog";
import { cn } from "@/lib/utils";

export function PolicyRuleCard({
  ev,
  animateIn,
  delayMs,
}: {
  ev: PolicyEvaluationDTO;
  animateIn?: boolean;
  delayMs?: number;
}) {
  const { tone, toneBg, Icon, label } = (() => {
    switch (ev.result) {
      case "pass":
        return { tone: "text-success", toneBg: "border-success/25 bg-success/5", Icon: CheckCircle2, label: "Pass" };
      case "warn":
        return { tone: "text-warn", toneBg: "border-warn/30 bg-warn/5", Icon: AlertTriangle, label: "Warning" };
      case "fail":
        return { tone: "text-destructive", toneBg: "border-destructive/30 bg-destructive/5", Icon: XOctagon, label: "Fail" };
    }
  })();
  return (
    <Card
      className={cn(
        "p-5",
        toneBg,
        animateIn && "animate-in fade-in slide-in-from-bottom-2 duration-500",
      )}
      style={
        animateIn
          ? { animationDelay: `${delayMs ?? 0}ms`, animationFillMode: "both" }
          : undefined
      }
    >
      <div className="flex items-start gap-3">
        <Icon className={cn("h-5 w-5 mt-0.5", tone)} strokeWidth={1.9} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={ev.result === "pass" ? "success" : ev.result === "warn" ? "warn" : "destructive"}>{label}</Badge>
            <h3 className="text-sm font-semibold">{ev.policy_name}</h3>
            <code className="text-xs text-muted-foreground">{ev.policy_code}</code>
            {ev.discretion_eligible && (
              <Badge variant="outline" className="text-accent border-accent/30">
                Officer discretion permitted
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{ev.details}</p>
          {ev.citation_markdown && (
            <div>
              <ManualCitationLink anchor={ev.citation_anchor} title={ev.citation_title} bodyMarkdown={ev.citation_markdown} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
